import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/webpack";
import interact from "interactjs";
import SignatureCanvas from "react-signature-canvas";
import "../styles/signstyle.css";
import { Spinner } from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import { rgb } from "pdf-lib";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  ArrowBack,
  Save,
  ArrowBackIos,
  ArrowForwardIos,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  Button,
  Modal,
  CircularProgress,
} from "@mui/material";
import { useApiService } from "../services/apiServices";

const SignaturePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleApiRequest = useApiService();
  const queryParams = new URLSearchParams(location.search);
  const pdfUrl = queryParams.get("pdfUrl");
  const id = queryParams.get("id");
  const page = queryParams.get("page");

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    
  // PDF and signature overlay states
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [draggableItemsData, setDraggableItemsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const { accounts } = useMsal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef(null);

  // File input and signature drawing refs
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const sigCanvas = useRef(null);

  // UI state for modals
  const [modalOpen, setModalOpen] = useState(false);
  const [drawModalOpen, setDrawModalOpen] = useState(false);

  // Consolidated state for available signatures (store full objects)
  const [signatures, setSignatures] = useState([]);
  // Flag to trigger a refresh of signatures
  const [refreshSignatures, setRefreshSignatures] = useState(false);
  // Loading states for signature operations
  const [signaturesLoading, setSignaturesLoading] = useState(false);
  const [deletingSignatureId, setDeletingSignatureId] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Error state for signature uploads
  const [signatureError, setSignatureError] = useState("");

  const formInputs = {
    adminOfficer: "",
    status: "",
    fileAttachment: "",
  };

  // Fetch available signatures from API (runs on mount and when refreshSignatures changes)
  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        setSignaturesLoading(true);
        const response = await handleApiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/signature/all`,
          "GET"
        );
        const data = await response;
        console.log(data);
        // Save the full signature objects
        setSignatures(data);
      } catch (error) {
        console.error("Error loading signatures:", error);
      } finally {
        setSignaturesLoading(false);
      }
    };
    fetchSignatures();
  }, [refreshSignatures]);

  // Delete signature function that triggers a refresh
  const deleteSignature = async (signatureId) => {
    try {
      setDeletingSignatureId(signatureId);
      const url = `${process.env.REACT_APP_API_BASE_URL}/signature?id=${signatureId}`;
      await handleApiRequest(url, "DELETE");
      setRefreshSignatures((prev) => !prev);
    } catch (error) {
      console.error("Error deleting signature:", error);
    } finally {
      setDeletingSignatureId(null);
    }
  };

  // Load PDF document when pdfUrl changes
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        const decodedUrl = decodeURIComponent(pdfUrl);
        const response = await fetch(decodedUrl);
        if (!response.ok) throw new Error("Failed to load the PDF");
        const pdfBytes = await response.arrayBuffer();
        const pdf = await pdfjsLib.getDocument(pdfBytes).promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      } catch (error) {
        console.error("Error loading PDF:", error);
      } finally {
        setLoading(false);
      }
    };

    if (pdfUrl) {
      loadPDF();
    } else {
      console.error("PDF URL is not provided");
    }
  }, [pdfUrl]);

  // Render the current PDF page
  useEffect(() => {
    if (pdfDoc) renderPage(pageNum);
  }, [pdfDoc, pageNum]);

  const renderPage = async (num) => {
    if (!pdfDoc || rendering) return;
    setRendering(true);
    try {
      const page = await pdfDoc.getPage(num);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const container = document.getElementById("canvas-container");
      const containerWidth = container.clientWidth;
      const newScale = containerWidth / viewport.width;
      setScale(newScale);
      canvas.width = viewport.width * newScale;
      canvas.height = viewport.height * newScale;
      const renderContext = {
        canvasContext: context,
        viewport: page.getViewport({ scale: newScale }),
      };
      await page.render(renderContext).promise;
      Object.keys(draggableItemsData).forEach((id) => {
        const item = draggableItemsData[id];
        const element = document.getElementById(id);
        if (item.page === num && element) {
          element.style.display = "block";
          element.style.transform = `translate(${item.x}px, ${item.y}px)`;
          element.style.width = `${item.width}px`;
          element.style.height = `${item.height}px`;
        } else if (element) {
          element.style.display = "none";
        }
      });
    } catch (error) {
      console.error("Error rendering page:", error);
    } finally {
      setRendering(false);
    }
  };

  // Create a draggable & resizable signature container on the canvas
  const createSignatureContainer = (src, left, top, width, id,isAdminSignature = false) => {
    const containerDiv = document.createElement("div");
    containerDiv.className = "signature-container";
    containerDiv.style.position = "absolute";
    containerDiv.style.left = `${left}px`;
    containerDiv.style.top = `${top}px`;
    containerDiv.style.width = `${width}px`;
    containerDiv.style.boxSizing = "border-box";
    containerDiv.style.border = "none";
    containerDiv.style.borderRadius = "4px";
    containerDiv.id = id;

    const img = document.createElement("img");
    img.src = src;
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.display = "block";

    const closeBtn = document.createElement("div");
    closeBtn.innerHTML = "✖";
    closeBtn.className = "close-icon";
    closeBtn.style.position = "absolute";
    closeBtn.style.top = "0px";
    closeBtn.style.right = "0px";
    closeBtn.style.cursor = "pointer";
    closeBtn.style.color = "red";
    closeBtn.style.fontWeight = "bold";
    closeBtn.style.padding = "2px";
    closeBtn.style.backgroundColor = "transparent";
    closeBtn.style.zIndex = "10";
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      containerDiv.remove();
      setDraggableItemsData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    });

    containerDiv.appendChild(img);
    containerDiv.appendChild(closeBtn);
 let dateValue = null;
    // Conditionally add the date if it's an admin signature
 if (isAdminSignature) {
    // Create a date element
    dateValue = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const dateDiv = document.createElement("datediv");
    dateDiv.textContent = dateValue;

    // Dynamically set the font size based on the container's width
    const baseFontSize = 12; // Base font size for a default width
    const scaleFactor = width / 100; // Adjust based on the container's width
    dateDiv.style.fontSize = `${baseFontSize * scaleFactor}px`;

    dateDiv.style.textAlign = "center";
    dateDiv.style.marginTop = "5px";
    dateDiv.style.color = "rgb(0,0,0)";
    dateDiv.style.fontFamily = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    dateDiv.style.fontWeight = "bold";

    containerDiv.appendChild(dateDiv);
  }
    // Define eight dot positions so they are fixed in size and half-off the container.
    const initialDotSize = 6;
    const dotOffset = initialDotSize / 2;
    const dotPositions = [
      { name: "top-left", style: { top: `-${dotOffset}px`, left: `-${dotOffset}px` } },
      { name: "top-center", style: { top: `-${dotOffset}px`, left: "50%", transform: "translateX(-50%)" } },
      { name: "top-right", style: { top: `-${dotOffset}px`, right: `-${dotOffset}px` } },
      { name: "right-center", style: { right: `-${dotOffset}px`, top: "50%", transform: "translateY(-50%)" } },
      { name: "bottom-right", style: { bottom: `-${dotOffset}px`, right: `-${dotOffset}px` } },
      { name: "bottom-center", style: { bottom: `-${dotOffset}px`, left: "50%", transform: "translateX(-50%)" } },
      { name: "bottom-left", style: { bottom: `-${dotOffset}px`, left: `-${dotOffset}px` } },
      { name: "left-center", style: { left: `-${dotOffset}px`, top: "50%", transform: "translateY(-50%)" } },
    ];

    dotPositions.forEach((dotPos) => {
      const dot = document.createElement("div");
      dot.className = "border-dot";
      dot.style.position = "absolute";
      dot.style.width = `${initialDotSize}px`;
      dot.style.height = `${initialDotSize}px`;
      dot.style.backgroundColor = "rgba(76, 175, 80, 1)";
      dot.style.borderRadius = "50%";
      dot.style.pointerEvents = "none";
      Object.assign(dot.style, dotPos.style);
      containerDiv.appendChild(dot);
    });

    const canvasContainer = document.getElementById("canvas-container");
    canvasContainer.appendChild(containerDiv);

    img.onload = () => {
      const computedHeight = (width * img.naturalHeight) / img.naturalWidth;
      containerDiv.style.height = `${computedHeight}px`;
      setDraggableItemsData((prev) => ({
        ...prev,
        [id]: {
          x: left,
          y: top,
          page: pageNum,
          width: width,
          height: computedHeight,
          date: dateValue,
          src
        },
      }));
    };

    interact(containerDiv)
      .draggable({
        ignoreFrom: ".close-icon",
        listeners:
          {
            move(event) {
              const target = event.target;
              const dx = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
              const dy = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;
              target.style.transform = `translate(${dx}px, ${dy}px)`;
              target.setAttribute("data-x", dx);
              target.setAttribute("data-y", dy);
              setDraggableItemsData((prev) => ({
                ...prev,
                [id]: { ...prev[id], x: dx, y: dy },
              }));
            },
          },
        modifiers: [
          interact.modifiers.restrictRect({
            restriction: "#canvas-container",
            endOnly: true,
          }),
        ],
      })
      .resizable({
        edges: { left: true, right: true, top: true, bottom: true },
        margin: 10,
        modifiers: [
          interact.modifiers.restrictSize({
            min: { width: 50, height: 50 },
          }),
        ],
        inertia: true,
      })
      .on("resizemove", (event) => {
        const target = event.target;
        let x = parseFloat(target.getAttribute("data-x")) || 0;
        let y = parseFloat(target.getAttribute("data-y")) || 0;
        const newWidth = event.rect.width;
        const newHeight = event.rect.height;
        target.style.width = `${newWidth}px`;
        target.style.height = `${newHeight}px`;
        x += event.deltaRect.left;
        y += event.deltaRect.top;
        target.style.transform = `translate(${x}px, ${y}px)`;
        target.setAttribute("data-x", x);
        target.setAttribute("data-y", y);
        // Dynamically adjust the font size of the date
        const dateDiv = target.querySelector("datediv");
        if (dateDiv) {
          const baseFontSize = 12; // Base font size for a default width
          const scaleFactor = newWidth / 100; // Adjust based on the container's width
          dateDiv.style.fontSize = `${baseFontSize * scaleFactor}px`;
        }

        setDraggableItemsData((prev) => ({
          ...prev,
          [id]: { ...prev[id], width: newWidth, height: newHeight, x, y },
        }));
      });
  };

  // Handle signature file upload and add to canvas
  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      e.target.value = null;
      const reader = new FileReader();
      reader.onload = (event) => {
        const id = `signature-${Date.now()}`;
        console.log("Adding signature to canvas upload:",event.target.result);
        createSignatureContainer(event.target.result, 10, 10, 100, id,false);
      };
      reader.readAsDataURL(file);
    }
  };

  const addSignatureToCanvas = (signature) => {
    fetch(signature.signatureUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          let dataUrl = reader.result;
          
          // Extract the file extension from the URL
          const extension = signature.signatureUrl.split('.').pop().toLowerCase();
          
          // Ensure the correct MIME type based on the file extension
          if (extension === "png") {
            dataUrl = dataUrl.replace("data:image/*", "data:image/png");
          } else if (extension === "jpeg" || extension === "jpg") {
            dataUrl = dataUrl.replace("data:image/*", "data:image/jpeg");
          }
          
          const id = `signature-${Date.now()}`;
          console.log("Adding signature to canvas:", dataUrl);
          createSignatureContainer(dataUrl, 10, 10, 100, id,true);
        };
        reader.readAsDataURL(blob);
      })
      .catch((err) => {
        console.error("Failed to convert signature URL to data URL", err);
      });
  };

  // Save the PDF with overlaid signatures
  const savePDF = async (status) => {
    if (!pdfDoc) {
      console.error("PDF is not loaded. Cannot save.");
      return;
    }
    try {
      const pdfBytes = await fetch(decodeURIComponent(pdfUrl)).then((res) =>
        res.arrayBuffer()
      );
      const pdfDocToSave = await PDFDocument.load(pdfBytes);
      const helveticaBoldFont = await pdfDocToSave.embedFont("Helvetica-Bold");

      for (const [id, { x, y, page, width, height,date,src}] of Object.entries(
        draggableItemsData
      )) { 
         const pageToEdit = pdfDocToSave.getPages()[page - 1];
        const { height: pageHeight, width: pageWidth } = pageToEdit.getSize();
        const canvas = canvasRef.current;
        const canvasRect = canvas.getBoundingClientRect();
        const containerRect = canvas.parentElement.getBoundingClientRect();
        const canvasOffsetX = canvasRect.left - containerRect.left;
        const canvasOffsetY = canvasRect.top - containerRect.top;
        const offsetCorrectionX = 5;
        const offsetCorrectionY = 20;
        const adjustedX = x - canvasOffsetX + offsetCorrectionX;
        const adjustedY = y - canvasOffsetY + offsetCorrectionY;
        const pdfX = (adjustedX / (pageWidth * scale)) * pageWidth;
        const pdfY =
          pageHeight -
          ((adjustedY + height * 0.9) / (pageHeight * scale)) * pageHeight;
       
        const element = document.getElementById(id);

        const pdfWidthScaled = (width / (pageWidth * scale)) * pageWidth;
        const pdfHeightScaled = (height / (pageHeight * scale)) * pageHeight;
        
        
        if (!element) {
          console.error(`Element with id ${id} not found. Skipping.`);
          continue;
        }

        let imageBytes;
        if (src.startsWith("data:")) {
          const base64 = src.split(",")[1];
          imageBytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
            .buffer;
        } else {
          imageBytes = await fetch(src).then((res) => res.arrayBuffer());
        }
        const isPng = src.includes("image/png");

        const embeddedImage = isPng
          ? await pdfDocToSave.embedPng(imageBytes)
          : await pdfDocToSave.embedJpg(imageBytes);

        pageToEdit.drawImage(embeddedImage, {
          x: pdfX,
          y: pdfY,
          width: pdfWidthScaled,
          height: pdfHeightScaled,
        });

          const dateDiv = element.querySelector("datediv");
          let fontSizePx = 12; // fallback
          if (dateDiv) {
            const computedFontSize = window.getComputedStyle(dateDiv).fontSize;
            fontSizePx = parseFloat(computedFontSize);
          }

          // Scale the font size from canvas pixels to PDF units
          const scaleY = pageHeight / canvas.height;
          const fontSize = fontSizePx * scaleY;
          pageToEdit.drawText(date? date : "", {
           x: pdfX,
           y: pdfY - 10 ,
           size: fontSize,
           color: rgb(0, 0, 0),
           font: helveticaBoldFont, 
          });
       }
   
      const finalBytes = await pdfDocToSave.save();
      const blob = new Blob([finalBytes], { type: "application/pdf" });
      const file = new File([blob], "signed-document.pdf", {
        type: "application/pdf",
      });
      await handleFormSubmit(file, status);
    } catch (error) {
      console.error("Error saving PDF:", error);
    }
  };

  const handleFormSubmit = async (file, status) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("fileAttachment", file);
      formData.append("adminOfficer", accounts[0].name);
      formData.append("status", status);
      for (const key in formInputs) {
        formData.append(key, formInputs[key]);
      }
      await handleApiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/affidavit/update?documentId=${id}`,
        "PATCH",
        formData
      );
      sessionStorage.setItem("documentId", id);
      navigate("/admin/success");
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    if (page !== "all") {
      navigate(`/search-request?id=${encodeURIComponent(id)}`);
    } else {
      navigate(`/admin/request-list`);
    }
  };

  // Signature Menu Handlers
  const handleFileInputClick = () => {
    fileInputRef.current && fileInputRef.current.click();
  };
  const handleManageSignatures = () => {
    setModalOpen(true);
  };
  const handleDrawSignature = () => {
    setDrawModalOpen(true);
  };
  const handleSignatureUploadClick = () => {
    signatureInputRef.current && signatureInputRef.current.click();
  };

  // Upload a new signature to the consolidated list (and backend)
  const handleSignatureUploadToList = async (e) => {
    const file = e.target.files[0];
    if (file) {
      e.target.value = null;
      const formData = new FormData();
      formData.append("fileAttachment", file);
      try {
        setUploadingSignature(true);
        // Clear any previous error
        setSignatureError("");
        const response = await handleApiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/signature/create`,
          "POST",
          formData
        );
        console.log("Signature uploaded successfully:", response);
        // Refresh the list
        setRefreshSignatures((prev) => !prev);
      } catch (error) {
        console.error("Upload failed:", error);
        setSignatureError("Failed to upload signature. Please try again.");
      } finally {
        setUploadingSignature(false);
      }
    }
  };

  return (
    <div className="app">
      <div className="container-content">
        {loading ? (
          <div className="loading-indicator">Loading PDF...</div>
        ) : (
          <>
            <Box display="flex" flexDirection="column">
              <AppBar position="sticky" color="inherit" elevation={1} sx={{ zIndex: 'unset' }}>
                <Toolbar>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    flexWrap="wrap"
                    mt={4} 
                    mb={1} 
                    gap={2}          
                    sx={{
                      "& > *": {
                        flexShrink: 1, 
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center"
                    gap={isSmallScreen ? 0.5 : 1}>
                      <Tooltip title="Back">
                        <IconButton
                          onClick={handleBackClick}
                          sx={{ color: "#4caf50" }}
                        >
                          <ArrowBack />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Upload Signature">
                        <Button
                          variant="outlined"
                          onClick={handleFileInputClick}
                          sx={{
                          fontSize: isSmallScreen ? '0.65rem' : '0.875rem',
                          padding: isSmallScreen ? '2px 6px' : '6px 16px',
                          minWidth: 0,
                          borderColor: "#4caf50",
                          color: "#4caf50",
                          "&:hover": {
                            borderColor: "#388e3c",
                            backgroundColor: "#e8f5e9",
                          },
                        }}
                        >
                          Upload Signature
                        </Button>
                      </Tooltip>
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleSignatureUpload}
                      />
                      <Tooltip title="Manage Signatures">
                        <Button
                          variant="outlined"
                          onClick={handleManageSignatures}
                          sx={{
                          fontSize: isSmallScreen ? '0.65rem' : '0.875rem',
                          padding: isSmallScreen ? '2px 6px' : '6px 16px',
                          minWidth: 0,
                          borderColor: "#4caf50",
                          color: "#4caf50",
                          "&:hover": {
                            borderColor: "#388e3c",
                            backgroundColor: "#e8f5e9",
                          },
                        }}
                        >
                          Manage Signatures
                        </Button>
                      </Tooltip>
                      <Tooltip title="Draw Signature">
                        <Button
                          variant="outlined"
                          onClick={handleDrawSignature}
                          sx={{
                          fontSize: isSmallScreen ? '0.65rem' : '0.875rem',
                          padding: isSmallScreen ? '2px 6px' : '6px 16px',
                          minWidth: 0,
                          borderColor: "#4caf50",
                          color: "#4caf50",
                          "&:hover": {
                            borderColor: "#388e3c",
                            backgroundColor: "#e8f5e9",
                          },
                        }}
                        >
                          Draw Signature
                        </Button>
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Tooltip title="Previous Page">
                        <IconButton
                          onClick={() =>
                            setPageNum(pageNum > 1 ? pageNum - 1 : 1)
                          }
                          disabled={!pdfDoc || rendering || pageNum <= 1}
                         sx={{ color: "#4caf50", fontSize: isSmallScreen ? "0.75rem" : "inherit" }}
                        >
                           <ArrowBackIos fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2" color="textSecondary"  
                      mx={1}
                      sx={{ fontSize: isSmallScreen ? "0.7rem" : "1rem" }}
                    >
                        Page {pageNum} of {numPages || "?"}
                      </Typography>
                      <Tooltip title="Next Page">
                        <IconButton
                          onClick={() => setPageNum(pageNum + 1)}
                          disabled={!pdfDoc || rendering || pageNum >= numPages}
                           sx={{
                            color: "#4caf50",
                            fontSize: isSmallScreen ? "0.75rem" : "inherit",
                            padding: isSmallScreen ? "4px" : "8px",
                          }}
                        >
                           <ArrowForwardIos fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#e8f5e9",
                            color: "#4caf50",
                            fontSize: isSmallScreen ? '0.7rem' : '0.875rem',
                            padding: isSmallScreen ? '4px 8px' : '6px 16px',
                            minWidth: isSmallScreen ? 'auto' : '64px',
                            "&:hover": { backgroundColor: "#c8e6c9" },
                          }}
                        startIcon={<Save sx={{ 
                          color: "#4caf50",
                          fontSize: isSmallScreen ? '1rem' : '1.25rem',
                           }} />}
                        onClick={() => savePDF("Approved")}
                        disabled={!pdfDoc || rendering || isSubmitting}
                      >
                        Save
                        {isSubmitting && (
                          <span style={{ marginLeft: isSmallScreen ? "4px" : "8px" }}>
                            <Spinner />
                          </span>
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Toolbar>
              </AppBar>
              <div id="canvas-container" className="canvas-container">
                <canvas ref={canvasRef} style={{ width: "100%" }}></canvas>
              </div>
            </Box>
          </>
        )}
      </div>
      {/* Manage Signatures Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: "90vw",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              borderBottom: "1px solid #e0e0e0",
              pb: 1,
            }}
          >
            <Typography variant="h6">Manage Signatures</Typography>
            <Box display="flex" alignItems="center">
              {uploadingSignature && (
                <CircularProgress size={24} sx={{ color: "#4caf50", mr: 2 }} />
              )}
              <Button
                variant="contained"
                onClick={handleSignatureUploadClick}
                disabled={uploadingSignature}
                sx={{
                  backgroundColor: "#e8f5e9",
                  color: "#4caf50",
                  "&:hover": { backgroundColor: "#c8e6c9" },
                  mr: 1,
                }}
              >
                Add Signature
              </Button>
              <IconButton
                onClick={() => setModalOpen(false)}
                sx={{ color: "red" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Display error label if upload fails */}
          {signatureError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {signatureError}
            </Typography>
          )}
          {signaturesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress sx={{ color: "#4caf50" }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                p: 1,
                whiteSpace: "nowrap",
              }}
            >
              {signatures.length === 0 ? (
                <Typography sx={{ p: 2, color: "text.secondary" }}>
                  No signatures found. Add your first signature using the button above.
                </Typography>
              ) : (
                signatures.map((signature) => (
                  <Box
                    key={signature.id}
                    sx={{
                      position: "relative",
                      width: 120,
                      height: 80,
                      flexShrink: 0,
                    }}
                  >
                    <Paper
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        p: 1,
                        transition: "box-shadow 0.3s ease",
                        "&:hover": {
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      onClick={() => {
                        addSignatureToCanvas(signature);
                        setModalOpen(false);
                      }}
                    >
                      {deletingSignatureId === signature.id ? (
                        <CircularProgress size={24} sx={{ color: "#4caf50" }} />
                      ) : (
                        <img
                          src={signature.signatureUrl}
                          alt={`Signature ${signature.id}`}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Paper>
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        color: "red",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSignature(signature.id);
                      }}
                      disabled={deletingSignatureId === signature.id}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            ref={signatureInputRef}
            style={{ display: "none" }}
            onChange={handleSignatureUploadToList}
          />
        </Box>
      </Modal>
      {/* Draw Signature Modal */}
      <Modal open={drawModalOpen} onClose={() => setDrawModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex" }}>
          <Typography variant="h6" mb={2} sx={{ flexGrow: 1 }}>
            Draw Your Signature
          </Typography>
            <IconButton
              onClick={() => setDrawModalOpen(false)}
              sx={{ color: "red" , marginBottom: 0}}
            >
              <CloseIcon />
            </IconButton>
          </Box>
         
          <SignatureCanvas
            ref={sigCanvas}
            penColor="#000"
            canvasProps={{ width: 400, height: 200, className: "sigCanvas" }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
            <Button variant="outlined" onClick={() => sigCanvas.current.clear()}>
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const dataURL = sigCanvas.current.toDataURL();
                const id = `signature-${Date.now()}`;
                createSignatureContainer(dataURL, 10, 10, 100, id);
                setDrawModalOpen(false);
              }}
              sx={{
                backgroundColor: "#e8f5e9",
                color: "#4caf50",
                "&:hover": { backgroundColor: "#c8e6c9" },
              }}
            >
              Use
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setUploadingSignature(true);
                try {
                  const dataURL = sigCanvas.current.toDataURL();
                  const blob = await fetch(dataURL).then((r) => r.blob());
                  const file = new File([blob], "drawn-signature.png", { type: "image/png" });
                  const formData = new FormData();
                  formData.append("fileAttachment", file);
                  await handleApiRequest(
                    `${process.env.REACT_APP_API_BASE_URL}/signature/create`,
                    "POST",
                    formData
                  );
                  setRefreshSignatures((prev) => !prev);
                  setDrawModalOpen(false);
                } catch (error) {
                  console.error("Error saving drawn signature:", error);
                } finally {
                  setUploadingSignature(false);
                }
              }}
              disabled={uploadingSignature}
              sx={{
                backgroundColor: "#e8f5e9",
                color: "#4caf50",
                "&:hover": { backgroundColor: "#c8e6c9" },
              }}
            >
              {uploadingSignature ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default SignaturePage;